import type { AxiosRequestConfig } from "axios";
import { apiClient as backendAPIClient } from "./backendAPI";

interface Session {
	accessToken: string;
}

interface RefreshResponse {
	accessToken: string;
}

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
	_retry?: boolean;
}

interface FailedQueueItem {
	resolve: (token: string) => void;
	reject: (error: any) => void;
}

const apiClients: any[] = [backendAPIClient];

let isRefreshing = false;
let failedQueue: FailedQueueItem[] = [];

const processQueue = (error: any, accessToken: string | null = null): void => {
	failedQueue.forEach((prom) => {
		if (error) {
			prom.reject(error);
		} else if (accessToken) {
			prom.resolve(accessToken);
		}
	});
	failedQueue = [];
};

export const createInterceptors = (
	session: Session,
	onRefresh: (session: Session) => Promise<RefreshResponse>,
): void => {
	for (const apiClient of apiClients) {
		apiClient.interceptors.request.clear();
		apiClient.interceptors.response.clear();

		apiClient.interceptors.request.use(
			(config: AxiosRequestConfig) => {
				config.headers = config.headers || {};
				config.headers.Authorization = `Bearer ${session.accessToken}`;
				return config;
			},
			(error: any) => Promise.reject(error),
		);

		apiClient.interceptors.response.use(
			(response: any) => response,
			async (error: any) => {
				const originalRequest = error.config as CustomAxiosRequestConfig;
				if (
					error.response &&
					error.response.status === 401 &&
					!originalRequest._retry &&
					!error.request.responseURL.includes("auth/refresh")
				) {
					if (isRefreshing) {
						try {
							const accessToken: string = await new Promise<string>(
								(resolve, reject) => {
									failedQueue.push({ resolve, reject });
								},
							);
							originalRequest.headers = originalRequest.headers || {};
							originalRequest.headers["Authorization"] =
								`Bearer ${accessToken}`;
							return apiClient(originalRequest);
						} catch (err) {
							return Promise.reject(err);
						}
					}
					originalRequest._retry = true;
					isRefreshing = true;
					try {
						const data = await onRefresh(session);
						apiClient.defaults.headers.common["Authorization"] =
							`Bearer ${data.accessToken}`;
						originalRequest.headers = originalRequest.headers || {};
						originalRequest.headers["Authorization"] =
							`Bearer ${data.accessToken}`;
						processQueue(null, data.accessToken);
						return apiClient(originalRequest);
					} catch (err) {
						processQueue(err, null);
						return Promise.reject(err);
					} finally {
						isRefreshing = false;
					}
				}
				return Promise.reject(error);
			},
		);
	}
};

export const removeInterceptors = (): void => {
	for (const apiClient of apiClients) {
		apiClient.interceptors.request.clear();
		apiClient.interceptors.response.clear();
	}
};
